import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DocumentLine } from '../document-line/document-line.model'; // Ensure import
import { HttpHelpService } from 'src/app/Data/service/http-help.service';
import { Product } from '../../shared/product-select/product-select.model';
import { finalize } from 'rxjs/operators'; // Import finalize

// Keep LineSummary interface
export interface LineSummary {
	amount_before_discount: number;
	all_discount: number;
	payment_amount: number; // Subtotal after discount
	all_vat_amount: number;
	payment_amount_including_vat: number; // Grand total for lines
}

@Component({
	selector: 'app-document-lines',
	templateUrl: './document-lines.component.html',
	styleUrls: ['./document-lines.component.scss']
})
export class DocumentLinesComponent implements OnInit, OnChanges, OnDestroy {
	@Input() parentForm!: FormGroup;
	@Input() submitted: boolean = false;
	@Input() debug: boolean = false;
	@Input() vatPercentage: number = 0;

	@Output() summaryChanged = new EventEmitter<LineSummary>();
	@Output() linesChanged = new EventEmitter<void>();

	displayAddProductModal: boolean = false;
	private pendingAddedProduct: any = null;
	currentSummary: LineSummary | null = null;
	lineControls: FormGroup[] = []; // Property to hold controls specifically for the table
	productsList: Product[] = [];
	isFetchingProducts: boolean = false; // Add flag to prevent concurrent fetches
	linesClosedStates: boolean[] = [];

	private maximizedOnce: boolean = false;

	private destroy$ = new Subject<void>();

	// REMOVED: globalVatIncluded - Each product now has individual VAT toggle

	constructor(
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private translate: TranslateService,
		private httpHelpService: HttpHelpService
	) { }

	/**
	 * @description Angular lifecycle hook called after every check of the component's view.
	 * Used here to maximize the dialog automatically on small screens when it first opens.
	 */
	ngAfterViewChecked(): void {
		if (
			this.displayAddProductModal &&
			window.innerWidth < 768 &&
			!this.maximizedOnce
		) {
			this.clickMaximizeButton();
			this.maximizedOnce = true;
		}
	}

	/**
	 * @protected
	 * @description Programmatically clicks the maximize button of the PrimeNG dialog.
	 * Used for responsive behavior on small screens.
	 */
	protected clickMaximizeButton() {
		// Note: This relies on PrimeNG's v13 internal CSS classes and might break with updates.
		const dialogEl = document.querySelector('.p-dialog') as HTMLElement;
		if (dialogEl) {
			const maximizeBtn = dialogEl.querySelector('.p-dialog-header-maximize') as HTMLElement;
			if (maximizeBtn) {
				maximizeBtn.click();
			}
		}
	}

	ngOnInit(): void {
		// Set a global flag to indicate this component is active
		(window as any).documentLinesComponentActive = true;
		(window as any).documentLinesComponent = this;
		
		if (!this.parentForm) {
			console.error("[ERROR/DocLines] ngOnInit ERROR: parentForm is missing!");
			return;
		}
		if (!this.linesArray) {
			console.error("[ERROR/DocLines] ngOnInit ERROR: lines FormArray is missing in parentForm!");
			return;
		}
		this.updateLineControlsProperty();

		if (this.linesArray.length === 0 && window.innerWidth >= 768) {
			this.addLine();
		}

		 // Ensure all pre-existing lines are closed
		this.linesClosedStates = this.getLineControlsInternal().map(() => true);
		
		// DISABLED: Automatic calculations were causing infinite cycles and floating-point errors
		// Calculations are now only triggered manually when needed

		// Listen for custom events from line components to trigger summary calculation
		window.addEventListener('lineCalculationChanged', (event: any) => {
			this.calculateLinesSummary();
		});

		// DISABLED: FormArray triggers were causing infinite calculation cycles
		// Summary calculations are now only triggered by custom events from line components

		// Perform initial calculation
		this.calculateLinesSummary();
		this.fetchProducts();

		// REMOVED: Global VAT initialization - Each product now has individual VAT toggle
	}

	fetchProducts(): void {
		if (this.isFetchingProducts) {
			return; // Prevent concurrent fetches
		}
		this.isFetchingProducts = true;
		this.httpHelpService.getAllActiveProductsBasedOnMerchantId()
		.pipe(
			finalize(() => {
				this.isFetchingProducts = false; // Reset flag when done (success or error)
				this.cdr.markForCheck(); // Ensure UI updates after fetch completes
			})
		)
		.subscribe({
			next: (res: Product[]) => {
				const list = res || [];
				const pending = this.pendingAddedProduct;
				if (pending?.product_id && !list.some((p) => +p.product_id === +pending.product_id)) {
					this.productsList = [pending, ...list];
				} else {
					this.productsList = list;
				}
				this.pendingAddedProduct = null;
			},
			error: (error) => {
				console.error('[ERROR/DocLines] fetchProducts: Error loading products:', error);
				this.productsList = []; // Clear list on error
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['parentForm'] && !changes['parentForm'].firstChange) {
			 if (!this.parentForm) {
				 console.error("[ERROR/DocLines] ngOnChanges ERROR: New parentForm is missing!");
				 return;
			 }
			 if (!this.parentForm.get('lines')) { // Check specifically for 'lines' FormArray
				 console.error("[ERROR/DocLines] ngOnChanges ERROR: New parentForm does NOT contain 'lines' FormArray!");
				 return;
			 }

			 this.destroy$.next(); // Trigger unsubscription from previous array's valueChanges
			 // Re-run init logic which includes updateLineControlsProperty and subscribing
			 this.ngOnInit(); // Re-initializes subscriptions and calculations
		}
if (changes['productsList'] && this.debug) {
			}
	}

	ngOnDestroy(): void {
		// Clear global reference
		(window as any).documentLinesComponentActive = false;
		(window as any).documentLinesComponent = null;
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Getter for easy access to the lines FormArray */
	get linesArray(): FormArray {
		return this.parentForm?.get('lines') as FormArray;
	}

	/** Internal helper to get controls array */
	private getLineControlsInternal(): FormGroup[] {
		return (this.linesArray?.controls as FormGroup[]) ?? [];
	}

	/** Updates the lineControls property with the current controls from the FormArray */
	private updateLineControlsProperty(): void {
		if (!this.linesArray) {
			this.lineControls = [];
			return;
		}
		// Assign a new array reference to ensure change detection picks it up
		const newControls = [...this.getLineControlsInternal()];
		this.lineControls = newControls;
	}

	/**
	 * Creates the basic structure for a new line FormGroup, aligning with the DocumentLine interface.
	 * @param initialData Optional data to populate the new line (e.g., when loading existing document)
	 */
	createLineGroupStructure(initialData?: Partial<DocumentLine>): FormGroup {
		const defaultDiscountType = 'amount';
		const lineIndex = this.linesArray?.length ?? 0;
		const newLine = this.fb.group({
			// Align controls with DocumentLine interface properties
			sold_item_id: [initialData?.sold_item_id || null],
			// Initialize product_id normally (enabled by default)
			product_id: [initialData?.product_id || null, Validators.required],
			item_name: [initialData?.item_name || ''],
			description: [initialData?.description || ''],
			quantity: [initialData?.quantity || 1, [Validators.required, Validators.min(0.1)]],
			price_per_unit: [initialData?.price_per_unit || 0, [Validators.required, Validators.min(0)]],
			discount: [initialData?.discount || 0, [Validators.min(0)]],
			discountType: [initialData?.discountType || defaultDiscountType, Validators.required],
			isPresenting: [initialData?.isPresenting ?? (initialData?.discountType === 'percentage')], // ✅ true for percentage, false for amount
			sub_total: [{ value: initialData?.sub_total || 0, disabled: true }], // Calculated - Keep disabled
			vat_rate: [{ value: initialData?.vat_rate ?? this.vatPercentage, disabled: true }], // Set externally - Keep disabled
			vat_amount: [{ value: initialData?.vat_amount || 0, disabled: true }], // Calculated - Keep disabled
			// Ensure total_amount is NOT disabled
			total_amount: [initialData?.total_amount || 0, [Validators.required, Validators.min(0)]], // <-- Make sure it's just the value + validators
			isVatIncluded: [initialData?.isVatIncluded || false],
			// Optional fields
			category: [initialData?.category || null],
			measure_unit_description: [initialData?.measure_unit_description || ''],
			measurement_unit_id: [initialData?.measurement_unit_id || null],
			catalog_id: [initialData?.catalog_id || ''],
			index: [initialData?.index ?? lineIndex] // Use calculated index
		});
		return newLine;
	}

	/** Adds a new line group to the FormArray */
	addLine(initialData?: Partial<DocumentLine>): void { // Accept optional initialData
		if (!this.linesArray) {
			console.error('[ERROR/DocLines] addLine ERROR: Cannot add line, linesArray is missing!');
			return;
		}
		// Close all lines
		this.linesClosedStates = this.getLineControlsInternal().map(() => true);

		const newLineGroup = this.createLineGroupStructure({
			...initialData,
			isVatIncluded: false // Default to VAT excluded for new products
		});
		this.linesArray.push(newLineGroup);
		this.updateLineControlsProperty();
		this.linesChanged.emit();
		this.cdr.detectChanges();
	}

	/** Removes a line group from the FormArray */
	removeLine(index: number): void {
		if (!this.linesArray) {
			console.error('[ERROR/DocLines] removeLine ERROR: Cannot remove line, linesArray is missing!');
			return;
		}
		if (index >= 0 && index < this.linesArray.length) {
			this.linesArray.removeAt(index);
			this.updateLineControlsProperty();
			this.linesClosedStates.splice(index, 1);
			this.linesChanged.emit();
			this.cdr.detectChanges(); // Trigger change detection
		}
	}

	/**
	 * Calculates the summary totals for all lines based on their current values
	 * and emits the result.
	 */
	calculateLinesSummary(): void {
		try {
		let totalBeforeDiscount = 0;
		let totalDiscountAmount = 0;
		let totalSubTotal = 0;
		let totalVatAmount = 0;
		let totalAmountIncludingVat = 0;

		this.getLineControlsInternal().forEach((lineGroup, index) => {
			// Read the ALREADY CALCULATED values that are displayed in the line item inputs
			// These are the values that the user sees and that are correct
			const lineSubTotal = lineGroup.get('sub_total')?.value ?? 0;
			const lineVatAmount = lineGroup.get('vat_amount')?.value ?? 0;
			const lineTotalAmount = lineGroup.get('total_amount')?.value ?? 0;
			const qty = lineGroup.get('quantity')?.value ?? 0;
			const price = lineGroup.get('price_per_unit')?.value ?? 0;
			const discountValue = lineGroup.get('discount')?.value ?? 0;
			
			
			// Get the line item properties first
			const actualDiscount = lineGroup.get('discount')?.value ?? 0;
			const discountType = lineGroup.get('discountType')?.value ?? 'amount';
			const isVatIncluded = lineGroup.get('isVatIncluded')?.value ?? false;
			const vatRate = lineGroup.get('vat_rate')?.value ?? 0;
			
			const grossLine = qty * price;
			let lineTotalBeforeDiscount = grossLine;
			if (isVatIncluded && vatRate > 0) {
				lineTotalBeforeDiscount = grossLine / (1 + vatRate / 100);
			}

			let lineDiscountAmount = 0;
			if (discountType === 'percentage') {
				const pctBase = isVatIncluded ? grossLine : lineTotalBeforeDiscount;
				lineDiscountAmount = pctBase * (actualDiscount / 100);
			} else {
				lineDiscountAmount = actualDiscount;
			}

			totalBeforeDiscount += lineTotalBeforeDiscount;
			totalDiscountAmount += lineDiscountAmount;
			totalSubTotal += lineSubTotal; // Use the DISPLAYED subtotal
			totalVatAmount += lineVatAmount; // Use the DISPLAYED VAT
			totalAmountIncludingVat += lineTotalAmount; // Use the DISPLAYED total
		});

		// Round to 2 decimal places to avoid floating-point precision errors
		const roundedTotalBeforeDiscount = Math.round(totalBeforeDiscount * 100) / 100;
		const roundedTotalDiscountAmount = Math.round(totalDiscountAmount * 100) / 100;
		const roundedTotalSubTotal = Math.round(totalSubTotal * 100) / 100;
		const roundedTotalVatAmount = Math.round(totalVatAmount * 100) / 100;
		const roundedTotalAmountIncludingVat = Math.round(totalAmountIncludingVat * 100) / 100;

		const summary: LineSummary = {
			amount_before_discount: roundedTotalBeforeDiscount,
			all_discount: roundedTotalDiscountAmount,
			payment_amount: roundedTotalSubTotal,
			all_vat_amount: roundedTotalVatAmount,
			payment_amount_including_vat: roundedTotalAmountIncludingVat
		};

		// Update local property for footer display
		this.currentSummary = summary;

		this.summaryChanged.emit(summary); // Emit the calculated summary
		this.cdr.detectChanges(); // Update footer display
		} catch (error) {
			console.error('[DEBUG/DocumentLines] Error in calculateLinesSummary:', error);
		}
	}

	/** Company on the open document, used to prefill the add-product dialog. */
	get documentCompanyId(): number | null {
		const raw = this.parentForm?.get('head.company_id')?.value;
		const n = Number(raw);
		return Number.isFinite(n) && n > 0 ? n : null;
	}

	/** Opens the Add Product modal */
	showAddProductDialog(): void {
		this.displayAddProductModal = true;
	}

	/** Placeholder method for when a product is added via the modal */
	onProductAdded(newProduct: any): void {
		this.displayAddProductModal = false;

		if (!newProduct || !newProduct.product_id) {
			console.error('[ERROR/DocLines] onProductAdded: Invalid product data received from modal.');
			return;
		}

		this.pendingAddedProduct = newProduct;
		this.productsList = [
			newProduct,
			...this.productsList.filter((p) => +p.product_id !== +newProduct.product_id),
		];

		// 1. Re-fetch the product list to include the new one
		this.fetchProducts(); // This now uses the finalize operator

		// It's generally better to add the line *after* the fetch completes,
		// but fetchProducts doesn't return the observable here.
		// Let's try adding the line immediately, relying on the updated productsList
		// being available when the child component initializes or updates.

		// 2. Prepare line data (ensure correct fields are used)
		const lineData: Partial<DocumentLine> = {
			product_id: newProduct.product_id,
			// Use the localized names/descriptions provided by the modal's output event
			item_name: newProduct.item_name, // Assuming modal provides this
			description: newProduct.description, // Assuming modal provides this
			quantity: 1, // Default
			price_per_unit: newProduct.price_per_unit, // Use price calculated/provided by modal
			discount: 0, // Default
			vat_rate: newProduct.vat_rate ?? this.vatPercentage, // Use rate from modal or current default
			isVatIncluded: newProduct.isVatIncluded || false, // If modal tracks this, use it
			catalog_id: newProduct.catalog_id || newProduct.product_code, // Use catalog_id or fallback
			category: newProduct.categories_id,
			measurement_unit_id: newProduct.measurement_unit_id
		};

		// 3. Add the new line to the form array
		this.addLine(lineData);
	}

	/**
	 * Handler for when a line toggles VAT included/excluded.
	 * REMOVED: Global VAT handling - Each product now has individual VAT toggle.
	 */
	onVatIncludedChange(newValue: boolean): void {
		// REMOVED: Global VAT handling - Each product now has individual VAT toggle
		}

	/**
	 * Adds manual triggers for FormArray changes to ensure summary updates.
	 * This ensures calculations happen when lines are added, removed, or modified.
	 * DISABLED: This was causing infinite calculation cycles.
	 */
	private addManualFormArrayTriggers(): void {
		// DISABLED: FormArray triggers were causing infinite calculation cycles
		// Summary calculations are now only triggered by custom events from line components
		// NO CODE HERE - completely disabled to prevent infinite cycles
	}
}